import { useState, useEffect } from "react";
import { ShieldX, MapPin, Smartphone, Globe, ExternalLink } from "lucide-react";
import axios from "axios";
import { SafeHtml } from "../components/SafeHtml";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlockedPage = ({ reason, targetingData }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/targeting/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update page title if custom title is set
  useEffect(() => {
    const pageTitle = targetingData?.blocked_page_title || settings?.blocked_page_title;
    if (pageTitle) {
      document.title = pageTitle;
    }
    
    // Update meta description if set
    const pageDesc = targetingData?.blocked_page_description || settings?.blocked_page_description;
    if (pageDesc) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = pageDesc;
    }
  }, [settings, targetingData]);

  // Wait for settings to load
  if (loading) {
    return null;
  }

  // If settings indicate redirect, handle it
  if (settings?.blocked_action === "redirect" && settings?.redirect_url) {
    window.location.href = settings.redirect_url;
    return null;
  }

  // Custom HTML content - "Özel İçerik Göster" seçeneği
  const blockedAction = settings?.blocked_action;
  const customContent = settings?.custom_blocked_content || targetingData?.custom_content;
  
  // Show custom content if action is "custom" and content exists
  if (blockedAction === "custom" && customContent && customContent.trim().length > 0) {
    return (
      <SafeHtml
        className="min-h-screen"
        style={{ background: '#111827' }}
        html={customContent}
      />
    );
  }

  // Get display values (prioritize targetingData, fallback to settings)
  const title = targetingData?.placeholder_title || settings?.placeholder_title || "İçerik Mevcut Değil";
  const message = targetingData?.placeholder_message || settings?.placeholder_message || "Bu içerik bölgenizde veya cihazınızda mevcut değildir.";
  const blockedReason = targetingData?.reason || reason;
  
  // New customization fields
  const buttonText = targetingData?.blocked_button_text || settings?.blocked_button_text;
  const buttonUrl = targetingData?.blocked_button_url || settings?.blocked_button_url;
  const footerText = targetingData?.blocked_footer_text || settings?.blocked_footer_text;

  // Default placeholder page
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f] flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-amber-500/10 rounded-full flex items-center justify-center mb-6">
            {blockedReason === "country_blocked" ? (
              <MapPin className="h-12 w-12 text-amber-500" />
            ) : blockedReason === "device_blocked" ? (
              <Smartphone className="h-12 w-12 text-amber-500" />
            ) : blockedReason === "fake_bot_detected" || blockedReason === "generic_bot" ? (
              <Globe className="h-12 w-12 text-blue-500" />
            ) : (
              <ShieldX className="h-12 w-12 text-amber-500" />
            )}
          </div>
          <h1 className="text-3xl font-chivo font-black text-white mb-2">{title}</h1>
        </div>

        {/* Message */}
        <p className="text-zinc-400 mb-8">{message}</p>

        {/* Reason indicator - don't show for bots */}
        {blockedReason !== "fake_bot_detected" && blockedReason !== "generic_bot" && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6 inline-flex items-center gap-2">
            {blockedReason === "country_blocked" && (
              <>
                <Globe className="h-5 w-5 text-blue-400" />
                <span className="text-zinc-400 text-sm">Bölge: {targetingData?.country || "Bilinmiyor"}</span>
              </>
            )}
            {blockedReason === "device_blocked" && (
              <>
                <Smartphone className="h-5 w-5 text-purple-400" />
                <span className="text-zinc-400 text-sm">Cihaz: {targetingData?.device || "Bilinmiyor"}</span>
              </>
            )}
          </div>
        )}

        {/* Custom Button */}
        {buttonText && buttonUrl && (
          <div className="mb-8">
            <a
              href={buttonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-black font-chivo font-bold px-6 py-3 rounded-lg transition-colors"
            >
              {buttonText}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}

        {/* Footer */}
        <p className="text-xs text-zinc-600">
          {footerText || "Erişim kısıtlaması uygulanmıştır"}
        </p>
      </div>
    </div>
  );
};

export default BlockedPage;
